import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { checkServerConnection, refreshToken } from '../lib/api';
import { 
  saveOfflineAuth, 
  getOfflineAuth, 
  hashPassword,
  saveOfflineData 
} from '../hooks/useNetworkStatus';

const AuthContext = createContext(null);

// Token refresh interval - refresh every 30 minutes
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000;
// Health check interval - check server every 30 seconds for faster detection
const HEALTH_CHECK_INTERVAL = 30 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionError, setConnectionError] = useState(false);
  const refreshIntervalRef = useRef(null);
  const healthCheckRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Clear all intervals
  const clearAllIntervals = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    if (healthCheckRef.current) {
      clearInterval(healthCheckRef.current);
      healthCheckRef.current = null;
    }
  }, []);

  // Perform token refresh
  const performTokenRefresh = useCallback(async () => {
    if (isRefreshingRef.current || !isOnline) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    isRefreshingRef.current = true;
    
    try {
      const response = await api.post('/auth/refresh');
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        if (response.data.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        setConnectionError(false);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [isOnline]);

  // Setup automatic token refresh
  const setupTokenRefresh = useCallback(() => {
    clearAllIntervals();
    
    refreshIntervalRef.current = setInterval(performTokenRefresh, TOKEN_REFRESH_INTERVAL);
    
    // Faster health check for better connectivity detection
    healthCheckRef.current = setInterval(async () => {
      if (!navigator.onLine) {
        setIsOnline(false);
        setConnectionError(true);
        return;
      }
      
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
          method: 'HEAD',
          signal: controller.signal,
          cache: 'no-store'
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          setIsOnline(true);
          setConnectionError(false);
        }
      } catch (error) {
        setIsOnline(false);
        setConnectionError(true);
      }
    }, HEALTH_CHECK_INTERVAL);
  }, [clearAllIntervals, performTokenRefresh]);

  // Check authentication on mount
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (!token || !savedUser) {
      setLoading(false);
      return;
    }
    
    // If offline, use cached data
    if (!navigator.onLine) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsOnline(false);
        setConnectionError(true);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
      setLoading(false);
      return;
    }
    
    try {
      const isConnected = await checkServerConnection();
      if (!isConnected) {
        setConnectionError(true);
        setIsOnline(false);
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
        } catch (e) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
        setLoading(false);
        return;
      }
      
      const response = await api.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      setConnectionError(false);
      setIsOnline(true);
      
      setupTokenRefresh();
      
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } else {
        setConnectionError(true);
        setIsOnline(false);
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
        } catch (e) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [setupTokenRefresh]);

  // Handle online/offline events
  useEffect(() => {
    checkAuth();
    
    const handleOnline = () => {
      console.log('[Auth] Back online');
      setIsOnline(true);
      setConnectionError(false);
      if (user) {
        performTokenRefresh();
      }
    };
    
    const handleOffline = () => {
      console.log('[Auth] Gone offline');
      setIsOnline(false);
      setConnectionError(true);
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user && navigator.onLine) {
        performTokenRefresh();
      }
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearAllIntervals();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Login function with offline support
  const login = async (email, password) => {
    // Check if online
    if (!navigator.onLine) {
      // Try offline login
      return offlineLogin(email, password);
    }
    
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('userEmail', userData.email);
      localStorage.setItem('userName', `${userData.prenom} ${userData.nom}`);
      
      // Save for offline login
      const passwordHash = await hashPassword(password);
      saveOfflineAuth(email, passwordHash, userData);
      
      setUser(userData);
      setConnectionError(false);
      setIsOnline(true);
      
      setupTokenRefresh();
      
      return userData;
    } catch (error) {
      // If network error, try offline login
      if (!error.response) {
        return offlineLogin(email, password);
      }
      throw error;
    }
  };

  // Offline login function
  const offlineLogin = async (email, password) => {
    const offlineAuth = getOfflineAuth();
    
    if (!offlineAuth) {
      throw new Error('Aucune donnée hors ligne disponible. Connectez-vous en ligne d\'abord.');
    }
    
    if (offlineAuth.email !== email) {
      throw new Error('Email incorrect. Utilisez le même email que votre dernière connexion.');
    }
    
    const passwordHash = await hashPassword(password);
    
    if (offlineAuth.passwordHash !== passwordHash) {
      throw new Error('Mot de passe incorrect.');
    }
    
    // Offline auth successful
    const userData = offlineAuth.userData;
    
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userEmail', userData.email);
    localStorage.setItem('userName', `${userData.prenom} ${userData.nom}`);
    
    setUser(userData);
    setIsOnline(false);
    setConnectionError(true);
    
    console.log('[Auth] Offline login successful');
    
    return userData;
  };

  // Biometric login
  const loginWithBiometric = async (authData) => {
    const { access_token, user: userData } = authData;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userEmail', userData.email);
    localStorage.setItem('userName', `${userData.prenom} ${userData.nom}`);
    
    setUser(userData);
    setConnectionError(false);
    setIsOnline(true);
    
    setupTokenRefresh();
    
    return userData;
  };

  // Register function
  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  };

  // Logout function
  const logout = useCallback(() => {
    clearAllIntervals();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    // Keep offline auth data for offline login
    setUser(null);
    setConnectionError(false);
    window.location.href = '/login';
  }, [clearAllIntervals]);

  // Check if user has role
  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.permissions && user.permissions.length > 0) {
      return user.permissions.includes(permission);
    }
    const rolePermissions = {
      admin: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'admin', 'settings', 'messages'],
      rh: ['dashboard', 'employees', 'planning', 'tasks', 'incidents', 'warnings', 'settings', 'messages'],
      responsable: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'settings', 'messages'],
      chauffeur: ['dashboard', 'planning', 'tasks', 'incidents', 'settings', 'messages']
    };
    const allowedPermissions = rolePermissions[user.role] || rolePermissions.chauffeur;
    return allowedPermissions.includes(permission);
  };

  // Refresh user data
  const refreshUser = async () => {
    if (!isOnline) return;
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('User refresh failed:', error);
    }
  };

  // Manual reconnect attempt
  const attemptReconnect = async () => {
    setLoading(true);
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isOnline,
      connectionError,
      login, 
      loginWithBiometric, 
      register, 
      logout, 
      hasRole, 
      hasPermission, 
      refreshUser,
      attemptReconnect 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
